import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './Lightbox.css';

// Full-screen photo viewer. Rendered via a portal to document.body so it escapes any
// section stacking context. Keyboard: Esc closes, ArrowLeft/Right navigate. Focus is
// trapped while open and restored to the trigger on close; body scroll is locked.
export default function Lightbox({ photos, startIndex = 0, title = '', onClose }) {
  const [index, setIndex] = useState(startIndex);
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const stripRef = useRef(null);
  const touchStartX = useRef(null);

  const total = photos.length;
  const go = useCallback(
    (delta) => setIndex((i) => (i + delta + total) % total),
    [total]
  );

  // Lock body scroll while open; restore focus to the previously focused element on unmount.
  useEffect(() => {
    const prevActive = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (prevActive instanceof HTMLElement) prevActive.focus();
    };
  }, []);

  // Keyboard controls + a simple focus trap kept within the dialog.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [go, onClose]);

  // Keep the active thumbnail scrolled into view within the filmstrip.
  useEffect(() => {
    const active = stripRef.current?.querySelector('.lightbox__thumb.is-active');
    active?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [index]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const photo = photos[index];
  const captionId = 'lightbox-title';

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-labelledby={captionId}
      onClick={onClose}
    >
      <div
        className="lightbox__dialog"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="lightbox__bar">
          <div className="lightbox__meta">
            <span id={captionId} className="lightbox__title">{title}</span>
          </div>
          <button
            type="button"
            className="lightbox__btn lightbox__close"
            aria-label="Close gallery"
            onClick={onClose}
            ref={closeBtnRef}
          >
            <Icon name="close" size={22} />
          </button>
        </div>

        <div className="lightbox__stage">
          {total > 1 && (
            <button
              type="button"
              className="lightbox__btn lightbox__nav lightbox__nav--prev"
              aria-label="Previous photo"
              onClick={() => go(-1)}
            >
              <Icon name="arrow" size={26} className="lightbox__nav-icon lightbox__nav-icon--flip" />
            </button>
          )}

          <img
            className="lightbox__img"
            src={photo.full}
            alt={`${title} — ${index + 1} of ${total}`}
            width={photo.w}
            height={photo.h}
            draggable="false"
          />

          {total > 1 && (
            <button
              type="button"
              className="lightbox__btn lightbox__nav lightbox__nav--next"
              aria-label="Next photo"
              onClick={() => go(1)}
            >
              <Icon name="arrow" size={26} className="lightbox__nav-icon" />
            </button>
          )}
        </div>

        {total > 1 && (
          <div className="lightbox__strip" ref={stripRef} aria-label="Photo thumbnails">
            {photos.map((p, i) => (
              <button
                key={i}
                type="button"
                className={`lightbox__thumb ${i === index ? 'is-active' : ''}`}
                aria-label={`Go to photo ${i + 1}`}
                aria-current={i === index}
                onClick={() => setIndex(i)}
              >
                <img src={p.thumb} alt="" loading="lazy" draggable="false" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
